import { get, isObjectLike, mapValues } from 'lodash';
import { errorCodes } from '../../utils/utils';
import pkg from '../../../package.json';
import { REQUESTS, USER_EVENT_TYPE } from '../../constants/backend';
import { getProfileCompanyName, getProfileUserName } from '../selectors/profile';
import type { RootState } from '../types';
import { logUserEvent } from './backendEventLogger';

const { name, version } = pkg;

const { UNEXPECTED_DAEMON_VERSION, MAIN_CONNECTION_CLOSED_ON_START } = errorCodes.background;

export interface RequestDescriptor {
    method?: string;
    route?: string;
    apiUrl?: string;
}

interface CountDownErrorInfo {
    code?: string | number;
    isImmediate?: boolean;
}

interface ReloadStateLike {
    backend?: {
        common?: { isReloading?: boolean };
        call?: { isStarted?: boolean };
    };
    background?: {
        common?: { isReloading?: boolean };
        call?: { isStarted?: boolean };
    };
}

const countDownByError = ({ code, isImmediate }: CountDownErrorInfo): number => {
    if (isImmediate) {
        return 1;
    }

    switch (code) {
        case MAIN_CONNECTION_CLOSED_ON_START:
            return 60;
        case UNEXPECTED_DAEMON_VERSION:
            return 30;
        default:
            return 5;
    }
};

export const types = {
    NETWORK_REQUEST: '@bg/NETWORK_REQUEST',
    UNAUTHORIZED_REQUEST: '@bg/UNAUTHORIZED_REQUEST',
    UPDATE_RELOAD_COUNT_DOWN: '@bg/UPDATE_RELOAD_COUNT_DOWN',
    ALL_SETTINGS_RECEIVED: '@bg/ALL_SETTINGS_RECEIVED',
    SHOW_NOTIFICATION: '@bg/SHOW_NOTIFICATION',
    RELOAD: '@bg/RELOAD',
    CLOSE_ALL_CONNECTIONS: '@bg/CLOSE_ALL_CONNECTIONS',
    SEND_LOG: '@bg/SEND_LOG',
    SALES_TRACKING_STATUS: '@bg/SALES_TRACKING_STATUS',
    MIC_DEVICE_NOT_AVAILABLE: '@bg/MIC_DEVICE_NOT_AVAILABLE'
};

export type BackgroundActionType = (typeof types)[keyof typeof types];

export interface NetworkRequestAction {
    type: typeof types.NETWORK_REQUEST;
    request: RequestDescriptor;
    payload?: unknown;
    onSuccess?: (response?: unknown) => void;
    onError?: (error?: unknown) => void;
    keepKeysCase?: boolean;
}

interface SendRequestOptions {
    payload?: unknown;
    onSuccess?: (response?: unknown) => void;
    onError?: (error?: unknown) => void;
    keepKeysCase?: boolean;
}

// keep the return type as a plain object so thunk middleware can handle it
export const sendRequest = (
    request: RequestDescriptor,
    { payload, onSuccess, onError, keepKeysCase = false }: SendRequestOptions
): NetworkRequestAction => ({
    type: types.NETWORK_REQUEST,
    request,
    payload,
    onSuccess,
    onError,
    keepKeysCase
});

// Minimal typings to avoid implicit unknown; integrates with redux-thunk
type ThunkDispatch = (action: unknown) => unknown;
type ThunkGetState = () => unknown;

export interface ReloadPayload {
    code?: string | number;
    isImmediate?: boolean;
    reason?: string;
    logout?: boolean;
    log?: boolean;
}

export const reload =
    ({ code, isImmediate = true, reason, logout = false, log = true }: ReloadPayload = {}) =>
    (dispatch: ThunkDispatch, getState: ThunkGetState): void => {
        console.info('!!!! RELOAD APP !!!!');

        if (reason === 'LOGOUT') {
            dispatch(logUserEvent(USER_EVENT_TYPE.LOGOUT));
        }

        const state = toReloadState(getState());
        if (get(state, 'backend.common.isReloading', get(state, 'background.common.isReloading', false))) {
            return;
        }

        const isCallStarted = get(state, 'backend.call.isStarted', get(state, 'background.call.isStarted', false));

        dispatch({
            type: types.RELOAD,
            payload: {
                code,
                isImmediate,
                reason,
                countdown: countDownByError({ code, isImmediate }),
                isCallStarted,
                logout,
                log
            }
        });
    };

export const updateReloadCountDown = () => ({
    type: types.UPDATE_RELOAD_COUNT_DOWN
});

export const allSettingReceived = () => ({
    type: types.ALL_SETTINGS_RECEIVED
});

export const micDeviceNotAvailable = () => ({
    type: types.MIC_DEVICE_NOT_AVAILABLE
});

type LogLevel = 'error' | 'warning' | 'info' | 'debug' | string;

interface LogErrorLike {
    message?: string;
    stack?: string;
    extra?: Record<string, unknown>;
    [key: string]: unknown;
}

export type LogError = Error | LogErrorLike | string;
type LogOptions = Record<string, unknown> | undefined;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toReloadState(value: unknown): ReloadStateLike {
    return isRecord(value) ? (value as ReloadStateLike) : {};
}

function toLogErrorLike(value: LogError): LogErrorLike | null {
    if (typeof value === 'string') {
        return null;
    }
    if (value instanceof Error) {
        return {
            message: value.message,
            stack: value.stack
        };
    }
    return isRecord(value) ? value : null;
}

interface LogBodyBase {
    level: LogLevel;
    timestamp: number;
    appName: string;
    appVersion: string;
    companyName: string | null;
    message: string;
    userName: string | null;
    stack?: string;
    options?: Record<string, unknown>;
}

export const sendLog = (state: RootState, error: LogError, level: LogLevel = 'error', options: LogOptions = undefined) => {
    let transformedObject: LogBodyBase = {
        level,
        timestamp: Math.round(new Date().getTime() / 1000),
        appName: name,
        appVersion: version,
        companyName: getProfileCompanyName(state),
        message: '',
        userName: getProfileUserName(state)
    };

    if (typeof error === 'string') {
        transformedObject = {
            ...transformedObject,
            message: error
        };
    } else {
        const anyError = toLogErrorLike(error);
        transformedObject = {
            ...transformedObject,
            message: anyError?.message || 'Unknown error',
            stack: anyError?.stack
        };
    }

    const extra = typeof error === 'string' ? {} : (toLogErrorLike(error)?.extra ?? {});

    transformedObject.options = mapValues({ ...extra, ...options }, (v: unknown): unknown => {
        if (isObjectLike(v)) {
            try {
                return JSON.stringify(v);
            } catch {
                return String(v);
            }
        }

        return v;
    }) as Record<string, unknown>;

    return sendRequest(REQUESTS.SEND_LOG, {
        payload: {
            body: transformedObject
        },
        keepKeysCase: true
    });
};
